import { useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import {
  APPOINTMENT_RESCHEDULE_MUTATION_KEY,
  type ModifyAppointmentResponseData,
  patchModifyAppointment,
} from '@/features/confirmation/api/patchConfirmation';
import type { RescheduleAppointmentData } from '@/features/confirmation/utils/ModifyAppointmentRequestData';
import useAppointmentStore from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';
import AgentSelectionLayout from './AgentSelectionLayout';

const RescheduleAgentSelection: React.FC = () => {
  const selectedAgent = useAppointmentStore((state) => state.selectedAgent);
  const {
    appointmentId,
    selectedStartTime,
    selectedEndTime,
    selectedDate,
    setConfirmedAppointment,
    setAppointmentId,
  } = useAppointmentStore();
  const { setFlowType } = useSessionStore();

  const navigate = useNavigate();
  const { id: appointmentIdParam } = useParams<{ id: string }>();

  const rescheduleAppointmentMutation = useMutation({
    mutationKey: APPOINTMENT_RESCHEDULE_MUTATION_KEY,
    mutationFn: (data: RescheduleAppointmentData) =>
      patchModifyAppointment(data),
    onSuccess: (
      data: ModifyAppointmentResponseData,
      variables: RescheduleAppointmentData,
    ) => {
      setAppointmentId(data.appointmentId);
      setConfirmedAppointment({
        agentInfo: selectedAgent,
        selectedDate,
        selectedStartTime: variables.startTime,
        selectedEndTime: variables.endTime,
      });
    },
  });

  const handleClickContinue = () => {
    if (!selectedAgent?.agentRecordId || !selectedStartTime || !selectedEndTime)
      return;

    const rescheduleData: RescheduleAppointmentData = {
      agentRecordId: selectedAgent.agentRecordId ?? '',
      agentResourceId: selectedAgent.resourceId ?? '',
      appointmentId: appointmentIdParam ?? appointmentId ?? '',
      serviceTerritoryId: selectedAgent.serviceTerritoryId ?? '',
      startTime: selectedStartTime,
      endTime: selectedEndTime,
      status: 'Rescheduled',
    };

    setFlowType('modify');
    rescheduleAppointmentMutation.mutate(rescheduleData);
    navigate('/confirmation');
  };

  return (
    <AgentSelectionLayout
      continueButtonDisabled={rescheduleAppointmentMutation.isPending}
      isRescheduleFlow={true}
      onClickContinue={handleClickContinue}
    />
  );
};

export default RescheduleAgentSelection;
